import React, {
    type ComponentRef,
    type KeyboardEvent as ReactKeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Pressable } from 'react-native';
import {
    BodyMediumEmphasized,
    ChevronDownIcon,
    Input,
    Shadows,
} from 'smoothie';
import { isWeb, Popover, View, YStack } from 'tamagui';

/** One row in the popover list (label shown; value is opaque to the parent). */
export interface MoneyMapPickerOption<T> {
    label: string;
    value: T;
    /** When false, row is shown disabled and cannot be chosen. */
    enabled?: boolean;
}

/** Map `KeyboardEvent.code` when `key` is missing (RN Web / older browsers). */
const CODE_TO_KEY: Record<string, string> = {
    ArrowDown: 'ArrowDown',
    ArrowUp: 'ArrowUp',
    ArrowLeft: 'ArrowLeft',
    ArrowRight: 'ArrowRight',
    Enter: 'Enter',
    NumpadEnter: 'Enter',
    Escape: 'Escape',
    Tab: 'Tab',
    Space: ' ',
};

/** Web / RN Web: read key + preventDefault/stopPropagation without breaking DOM `this`. */
function readWebKeyEvent(e: unknown): {
    key: string;
    altKey: boolean;
    preventDefault: () => void;
    stopPropagation: () => void;
} | null {
    if (e == null || typeof e !== 'object') {
        return null;
    }
    const ke = e as ReactKeyboardEvent<unknown> & {
        nativeEvent?: KeyboardEvent;
    };
    const dom = ke.nativeEvent;

    const code =
        (typeof ke.code === 'string' && ke.code) ||
        (dom != null && typeof dom.code === 'string' && dom.code) ||
        '';

    const keyFromEvent =
        (typeof ke.key === 'string' && ke.key) ||
        (dom != null && typeof dom.key === 'string' && dom.key) ||
        '';

    const key =
        keyFromEvent || (code !== '' ? (CODE_TO_KEY[code] ?? '') : '') || '';

    const preventDefault = () => {
        if (typeof ke.preventDefault === 'function') {
            ke.preventDefault();
        }
        if (dom != null && typeof dom.preventDefault === 'function') {
            dom.preventDefault();
        }
    };

    const stopPropagation = () => {
        if (typeof ke.stopPropagation === 'function') {
            ke.stopPropagation();
        }
        if (dom != null && typeof dom.stopPropagation === 'function') {
            dom.stopPropagation();
        }
    };

    return {
        key,
        altKey: Boolean(ke.altKey ?? dom?.altKey),
        preventDefault,
        stopPropagation,
    };
}

function optionRowEnabled<T>(
    option: MoneyMapPickerOption<T>,
    fieldDisabled: boolean,
): boolean {
    return !fieldDisabled && option.enabled !== false;
}

const MAX_DROPDOWN_HEIGHT = 320;
const MIN_DROPDOWN_HEIGHT = 96;
const DROPDOWN_VIEWPORT_PADDING = 16;

function getClientRect(
    node: unknown,
): ReturnType<HTMLElement['getBoundingClientRect']> | null {
    if (!isWeb || node == null || typeof node !== 'object') {
        return null;
    }

    const measurable = node as {
        getBoundingClientRect?: HTMLElement['getBoundingClientRect'];
    };

    if (typeof measurable.getBoundingClientRect !== 'function') {
        return null;
    }

    return measurable.getBoundingClientRect();
}

export interface GroveMoneyMapPickerFieldProps<T> {
    value: T;
    options: Array<MoneyMapPickerOption<T>>;
    onChange: (value: T) => void;
    disabled?: boolean;
    size?: 'regular' | 'tiny';
    /** Focus the trigger and open the list once (e.g. new table row). */
    autoFocusAndOpenOnMount?: boolean;
    /** Focus the trigger once without opening the list (e.g. add-income row with member picker). */
    autoFocusOnMount?: boolean;
    /** Fires when the trigger loses focus (e.g. Tab away); use for row-level persist / blur logic. */
    onTriggerBlur?: (event: unknown) => void;
}

/**
 * Grove Money Map: table-style read-only combobox (Smoothie `Input` + Tamagui `Popover`).
 * Keyboard (web): open with Enter / Space / arrows; navigate with arrows; confirm with Enter / Space; Escape closes.
 * Reusable across take-home pay, bills, assets, and other Money Map tabs.
 */
export const GroveMoneyMapPickerField = <T,>({
    value,
    options,
    onChange,
    disabled = false,
    size = 'regular',
    autoFocusAndOpenOnMount = false,
    autoFocusOnMount = false,
    onTriggerBlur,
}: GroveMoneyMapPickerFieldProps<T>) => {
    const inputRef = useRef<ComponentRef<typeof Input>>(null);
    const triggerRef = useRef<ComponentRef<typeof YStack>>(null);
    const hasAutoOpenedRef = useRef(false);
    const hasAutoFocusOnlyRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] =
        useState(MAX_DROPDOWN_HEIGHT);
    const [keyboardHighlightIndex, setKeyboardHighlightIndex] = useState<
        number | null
    >(null);
    const highlightRef = useRef(0);

    useEffect(() => {
        if (!autoFocusAndOpenOnMount || disabled) {
            return;
        }
        if (hasAutoOpenedRef.current) {
            return;
        }
        hasAutoOpenedRef.current = true;
        setOpen(true);
        const t = setTimeout(() => {
            inputRef.current?.focus?.();
        }, 0);
        return () => {
            clearTimeout(t);
        };
    }, [autoFocusAndOpenOnMount, disabled]);

    useEffect(() => {
        if (!autoFocusOnMount || disabled || autoFocusAndOpenOnMount) {
            return;
        }
        if (hasAutoFocusOnlyRef.current) {
            return;
        }
        hasAutoFocusOnlyRef.current = true;
        const t = setTimeout(() => {
            inputRef.current?.focus?.();
        }, 0);
        return () => {
            clearTimeout(t);
        };
    }, [autoFocusOnMount, autoFocusAndOpenOnMount, disabled]);

    useEffect(() => {
        if (!open) {
            setKeyboardHighlightIndex(null);
            setDropdownMaxHeight(MAX_DROPDOWN_HEIGHT);
            return;
        }
        const selectedIdx = options.findIndex((o) => o.value === value);
        let start = selectedIdx >= 0 ? selectedIdx : 0;
        const startOption = options[start];
        if (startOption == null || !optionRowEnabled(startOption, disabled)) {
            const found = options.findIndex((o) =>
                optionRowEnabled(o, disabled),
            );
            start = found >= 0 ? found : 0;
        }
        highlightRef.current = start;
        setKeyboardHighlightIndex(start);
    }, [open, options, value, disabled]);

    useEffect(() => {
        if (!open || !isWeb || typeof window === 'undefined') {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            const rect =
                getClientRect(triggerRef.current) ??
                getClientRect(inputRef.current);

            if (rect == null) {
                return;
            }

            const availableBelow =
                window.innerHeight - rect.bottom - DROPDOWN_VIEWPORT_PADDING;
            const availableAbove = rect.top - DROPDOWN_VIEWPORT_PADDING;
            const availableHeight = Math.max(availableBelow, availableAbove);
            const nextHeight = Math.min(
                MAX_DROPDOWN_HEIGHT,
                Math.max(MIN_DROPDOWN_HEIGHT, availableHeight),
            );

            setDropdownMaxHeight(nextHeight);
        });

        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [open]);

    const moveHighlight = useCallback(
        (delta: -1 | 1) => {
            if (options.length === 0) {
                return;
            }
            setKeyboardHighlightIndex((prev) => {
                let i =
                    prev ??
                    Math.max(
                        0,
                        options.findIndex((o) => o.value === value),
                    );
                const atI = options[i];
                if (atI == null) {
                    return prev;
                }
                if (!optionRowEnabled(atI, disabled)) {
                    const fi = options.findIndex((o) =>
                        optionRowEnabled(o, disabled),
                    );
                    i = fi >= 0 ? fi : 0;
                }
                for (let attempts = 0; attempts < options.length; attempts++) {
                    i = (i + delta + options.length) % options.length;
                    const candidate = options[i];
                    if (
                        candidate != null &&
                        optionRowEnabled(candidate, disabled)
                    ) {
                        highlightRef.current = i;
                        return i;
                    }
                }
                return prev;
            });
        },
        [disabled, options, value],
    );

    const commitSelection = useCallback(
        (index: number) => {
            const opt = options[index];
            if (opt == null || !optionRowEnabled(opt, disabled)) {
                return;
            }
            onChange(opt.value);
            setOpen(false);
        },
        [disabled, onChange, options],
    );

    const onTriggerKeyDown = useCallback(
        (e: unknown) => {
            if (!isWeb || disabled) {
                return;
            }
            const norm = readWebKeyEvent(e);
            if (norm == null) {
                return;
            }
            const { key, altKey, preventDefault, stopPropagation } = norm;

            if (open) {
                if (key === 'Escape') {
                    preventDefault();
                    stopPropagation();
                    setOpen(false);
                    return;
                }
                if (key === 'Tab') {
                    setOpen(false);
                    return;
                }
                if (key === 'ArrowDown') {
                    preventDefault();
                    stopPropagation();
                    moveHighlight(1);
                    return;
                }
                if (key === 'ArrowUp') {
                    preventDefault();
                    stopPropagation();
                    moveHighlight(-1);
                    return;
                }
                if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
                    preventDefault();
                    stopPropagation();
                    commitSelection(highlightRef.current);
                    return;
                }
                return;
            }

            const openKeys =
                key === 'Enter' ||
                key === 'ArrowDown' ||
                key === 'ArrowUp' ||
                key === ' ' ||
                key === 'Spacebar' ||
                (altKey && key === 'ArrowDown');

            if (openKeys) {
                preventDefault();
                stopPropagation();
                setOpen(true);
            }
        },
        [commitSelection, disabled, moveHighlight, open],
    );

    const displayLabel = useMemo(
        () => options.find((o) => o.value === value)?.label ?? '',
        [options, value],
    );

    const isEmphasized = !disabled && (open || focused);
    const triggerBorderWidth = isEmphasized ? 2 : 1;

    const emphasizedTriggerStyles = isEmphasized
        ? {
              borderColor: '$black' as const,
              borderWidth: 2,
              hoverStyle: {
                  borderColor: '$black' as const,
                  borderWidth: 2,
              },
              focusStyle: {
                  borderColor: '$black' as const,
                  borderWidth: 2,
                  ...(isWeb
                      ? {
                            outlineWidth: 0,
                            outlineStyle: 'none' as const,
                        }
                      : {}),
              },
          }
        : {};

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            strategy="fixed"
            stayInFrame
            allowFlip
            disableFocus={isWeb}
        >
            <Popover.Trigger asChild disabled={disabled}>
                <YStack
                    ref={triggerRef}
                    width="100%"
                    {...(isWeb
                        ? {
                              onKeyDownCapture: onTriggerKeyDown,
                          }
                        : {})}
                >
                    <Input
                        ref={inputRef}
                        width="100%"
                        size={size}
                        value={displayLabel}
                        editable={false}
                        disabled={disabled}
                        rightIcon={ChevronDownIcon}
                        cursor={disabled ? 'default' : 'pointer'}
                        borderWidth={triggerBorderWidth}
                        focusable={!disabled}
                        tabIndex={disabled ? -1 : 0}
                        onFocus={() => {
                            setFocused(true);
                        }}
                        onBlur={(blurEvent) => {
                            setFocused(false);
                            onTriggerBlur?.(blurEvent);
                        }}
                        {...(isWeb
                            ? {
                                  outlineWidth: 0,
                                  outlineStyle: 'none',
                                  accessibilityRole: 'combobox',
                                  accessibilityState: { expanded: open },
                              }
                            : {})}
                        onPressIcon={() => {
                            if (!disabled) {
                                setOpen((o) => !o);
                            }
                        }}
                        {...emphasizedTriggerStyles}
                    />
                </YStack>
            </Popover.Trigger>
            <Popover.Content
                borderWidth={0}
                padding={8}
                borderRadius={16}
                overflow="hidden"
                style={[Shadows.LARGE, { minWidth: 200 }]}
                elevate
                zIndex={100000}
                trapFocus={false}
                onOpenAutoFocus={(focusEvent) => {
                    focusEvent.preventDefault();
                }}
                {...(isWeb
                    ? {
                          // Standard combobox pattern: prevent mousedown from
                          // blurring the trigger before onPress fires. Without
                          // this, onTriggerBlur runs with stale draft values
                          // before onChange can update them.
                          onMouseDown: (e: { preventDefault: () => void }) => {
                              e.preventDefault();
                          },
                      }
                    : {})}
            >
                <Popover.ScrollView
                    maxHeight={dropdownMaxHeight}
                    width="100%"
                    showsVerticalScrollIndicator={false}
                >
                    <YStack
                        gap={4}
                        {...(isWeb
                            ? {
                                  accessibilityRole: 'list',
                              }
                            : {})}
                    >
                        {options.map((option, index) => {
                            const isSelected = option.value === value;
                            const rowDisabled = !optionRowEnabled(
                                option,
                                disabled,
                            );
                            const isKeyboardActive =
                                open &&
                                keyboardHighlightIndex === index &&
                                !rowDisabled;
                            return (
                                <Popover.Close
                                    key={`${option.label}-${index}`}
                                    asChild
                                >
                                    <Pressable
                                        disabled={rowDisabled}
                                        tabIndex={isWeb ? -1 : undefined}
                                        onPress={() => {
                                            if (!rowDisabled) {
                                                onChange(option.value);
                                                setOpen(false);
                                            }
                                        }}
                                    >
                                        <View
                                            opacity={rowDisabled ? 0.45 : 1}
                                            backgroundColor={
                                                isKeyboardActive
                                                    ? '$gray100'
                                                    : isSelected
                                                      ? '$gray200'
                                                      : '$white'
                                            }
                                            padding={12}
                                            borderRadius={12}
                                            hoverStyle={
                                                !isSelected && !rowDisabled
                                                    ? {
                                                          backgroundColor:
                                                              '$gray100',
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <BodyMediumEmphasized color="$gray1200">
                                                {option.label}
                                            </BodyMediumEmphasized>
                                        </View>
                                    </Pressable>
                                </Popover.Close>
                            );
                        })}
                    </YStack>
                </Popover.ScrollView>
            </Popover.Content>
        </Popover>
    );
};
